import React, {useEffect, useState} from "react";
import {useAIStateContext} from "../../../contexts/ai_chat_state_context";
import Tabs from "../Tabs";
import ChatWindow from "../ChatWindow";
import {Border, Ink, Radii, Shadows, Surface} from "../../../colors";
import {useGroupContext} from "../../../contexts/group_context";
import {Loader} from "../../Loader";

const ChatInterface: React.FC = () => {
    const {createChat, getChatsForCurrentGroup, chats, setChats, currentChat, setCurrentChat} = useAIStateContext()!;
    const {currentGroup} = useGroupContext()!;
    const [loading, setLoading] = useState(false);

    // Fetch chats for current group
    // Good pattern, this is how we should start to remove too many dependencies on the group obj
    // Use currentGroup?.id to avoid refetching when group object refreshes but ID is unchanged
    useEffect(() => {
        const fetchChats = async () => {
            if (!currentGroup) return;
            setLoading(true);
            try {
                const data = await getChatsForCurrentGroup();
                setChats(data);
                setCurrentChat(data.length > 0 ? data[0] : null);
            } catch (err) {
                console.error("Error fetching chats:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchChats();
    }, [currentGroup?.id]);

    const handleNewChat = async () => {
        const newChat = await createChat();
        setChats((prev) => [newChat, ...prev]);
        setCurrentChat(newChat);
    };

    return (
        <div className={`flex flex-col h-full w-full ${Radii.lg} ${Surface.base} ${Border.DEFAULT} ${Shadows.soft}`}>
            <div className="shrink-0">
                <Tabs onNewChat={handleNewChat}/>
            </div>

            <div className="flex-1 min-h-0 relative">
                {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader text="Loading chats..."/>
                    </div>
                ) : chats.length === 0 ? (
                    <div className={`flex flex-col items-center justify-center h-full text-center ${Ink.muted} space-y-3 p-6`}>
                        <h3 className={`text-lg font-semibold ${Ink.primary}`}>
                            No chats yet
                        </h3>
                        <p className={`text-sm ${Ink.muted} max-w-xs`}>
                            Start a new conversation by clicking{" "}
                            <span className={`font-medium ${Ink.brand}`}>+ New Chat</span>.
                            <br/> You can discuss cases, summarize documents, or draft
                            responses directly here.
                        </p>
                    </div>
                ) : (
                    currentChat && <ChatWindow currentChatId={currentChat.id}/>
                )}
            </div>
        </div>
    );
};

export default ChatInterface;
